import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, Loader2, Package, Printer, FolderOpen, CheckCircle2 } from "lucide-react";
import { Button } from "./ui/button";
import { getReportOffer, createReportCheckout } from "../lib/api";

const money = (v) => `$${Number(v || 0).toFixed(2)}`;

const Choice = ({ icon: Icon, title, price, children, action, testId }) => (
  <div data-testid={testId} className="rounded-2xl border border-emerald-100 bg-white p-4 flex flex-col">
    <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
      <div className="flex items-center gap-2 font-display font-bold text-[#092C15] min-w-0"><Icon size={18} className="text-sky-700 shrink-0" />{title}</div>
      <span className="text-xs font-bold uppercase tracking-wider text-sky-700">{price}</span>
    </div>
    <p className="mt-1.5 text-sm text-slate-600 flex-1">{children}</p>
    <div className="mt-3">{action}</div>
  </div>
);

export default function ReportOffer({ sessionId, refreshKey = 0 }) {
  const [offer, setOffer] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    getReportOffer(sessionId).then(setOffer).catch(() => setOffer(null));
  }, [sessionId, refreshKey]);

  if (!offer || (!offer.available && !offer.orders.length)) return null;
  const paid = (pkg) => offer.orders.find((o) => o.package === pkg && o.report_url);

  const buy = async (pkg) => {
    setBusy(pkg);
    try {
      const r = await createReportCheckout(sessionId, pkg);
      window.location.href = r.already_purchased ? r.report_url : r.checkout_url;
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Checkout is unavailable right now.");
      setBusy("");
    }
  };
  const btn = "w-full min-h-11 h-auto py-2.5 whitespace-normal rounded-xl font-bold tracking-wide";
  const digital = paid("digital");

  return (
    <section data-testid="report-offer" className="rounded-3xl bg-[#F3F8F5] border border-emerald-200 p-5">
      <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-sky-700">GreenFlow Plant Health &amp; Diagnostic Group</div>
      <h3 className="font-display text-xl sm:text-2xl font-bold text-[#092C15] mt-1">KEEP YOUR COMPLETE ASK DR. VERDANT REPORT</h3>
      <p className="mt-1.5 text-sm text-slate-600">{offer.case_url ? "Your specialist conversation contains valuable details that may not appear in your original photo assessment. Keep the complete findings, recommendations, resources, and action plan in a format you can save, print, or share." : "Keep your specialists' findings, recommendations, resources, and action plan in a format you can save, print, or share."}</p>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <Choice testId="offer-print" icon={Printer} title="Print Conversation" price="Free"
          action={<Button data-testid="offer-print-button" variant="outline" className={btn}
            onClick={() => window.open(`/case/${sessionId}/print`, "_blank", "noopener")}>PRINT CONVERSATION</Button>}>
          {offer.case_url ? "A clean printer-friendly copy of your completed assessment and specialist conversation." : "A clean printer-friendly copy of your specialist conversation."}
        </Choice>
        <Choice testId="offer-digital" icon={FileText} title="Personalized Diagnostic Brief" price={money(offer.prices.digital)}
          action={digital
            ? <Button data-testid="offer-digital-open" className={`${btn} bg-emerald-700 hover:bg-emerald-800`} onClick={() => { window.location.href = digital.report_url; }}><CheckCircle2 size={16} className="mr-2" />OPEN MY REPORT</Button>
            : <Button data-testid="offer-digital-button" disabled={!!busy} className={`${btn} bg-[#14532D] hover:bg-[#0F3F22]`} onClick={() => buy("digital")}>
                {busy === "digital" && <Loader2 size={16} className="mr-2 animate-spin" />}GET MY {money(offer.prices.digital).replace(".00", "")} DIGITAL REPORT</Button>}>
          {offer.case_url ? "A professionally organized, downloadable PDF, also emailed to you, with your photos, every specialist's findings and your action plan." : "A professionally organized, downloadable PDF, also emailed to you, with every specialist's findings and your action plan."}
        </Choice>
        <Choice testId="offer-printed" icon={Package} title="Printed Presentation Package" price={`${money(offer.prices.printed)} · Pilot`}
          action={<Button data-testid="offer-printed-button" variant="outline" disabled={!!busy || !offer.print_pilot || !!paid("printed")} className={btn} onClick={() => buy("printed")}>
            {busy === "printed" && <Loader2 size={16} className="mr-2 animate-spin" />}{paid("printed") ? "PILOT ORDER RECEIVED" : "ORDER A PRINTED PACKAGE"}</Button>}>
          Private-test pilot. Printed mailing is not active yet; pricing and delivery are still being finalized.
        </Choice>
        {offer.case_url && <Choice testId="offer-original" icon={FolderOpen} title="View My Original Assessment" price="Free"
          action={<Button data-testid="offer-original-button" variant="outline" className={btn}
            onClick={() => window.open(offer.case_url, "_blank", "noopener")}>VIEW MY ORIGINAL ASSESSMENT</Button>}>
          Opens your private case link, the same one already emailed to you.
        </Choice>}
      </div>
    </section>
  );
}
